import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal, viewChild } from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { EntityIdType } from "@front/m19-api-client";
import { singleStrategyInStrategyGroup, StrategyGroupEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, SpStrategyService, UserSelectionService } from "@front/m19-services";
import { ITab, ITabsComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ActivityComponent, ActivityFilter } from "@m19-board/activities/activity/activity.component";
import {
  AdvertisingStatsGridComponent,
  GlobalStrategyStats,
} from "@m19-board/insights/advertising-stats/advertising-stats-grid.component";
import { EditableTextComponent } from "@m19-board/shared/editable-text/editable-text.component";
import { UntilDestroy } from "@ngneat/until-destroy";
import { combineLatest, map } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { StrategyGroupProductsComponent } from "./strategy-products/strategy-group-products.component";
import { StrategyGroupStrategiesComponent } from "./strategy-strategies/strategy-group-strategies.component";
import { LoadingService } from "@m19-board/services/loading.service";
import { NavBreadcrumbDirective } from "@m19-board/containers/page-layout/nav-breadcrumb.directive";
import { StrategyService } from "@front/m19-injection-token";
import { SyncInfoComponent } from "@m19-board/strategies/sync-info/sync-info.component";
import { SpStrategyStatsGraphComponent } from "@m19-board/sponsored-product/sp-page/strategy-stats-graph/sp-strategy-stats-graph.component";
import { getCsvFileName } from "@m19-board/grid-config/grid-config";

enum SpGroupPageSection {
  PRODUCTS,
  CAMPAIGNS,
  STATS,
  ACTIVITIES,
  ADVANCED_SETTINGS,
}

@UntilDestroy()
@Component({
  templateUrl: "./sp-strategy-group-page.component.html",
  imports: [
    FormsModule,
    TranslocoDirective,
    EditableTextComponent,
    StrategyGroupProductsComponent,
    ITabsComponent,
    StrategyGroupStrategiesComponent,
    ActivityComponent,
    AdvertisingStatsGridComponent,
    NavBreadcrumbDirective,
    SyncInfoComponent,
    SpStrategyStatsGraphComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpStrategyGroupPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly spStrategyService = inject(SpStrategyService);
  private readonly strategyService = inject(StrategyService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly toastrService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthService);
  private readonly loadingService = inject(LoadingService);
  private readonly userSelectionService = inject(UserSelectionService);

  readonly destroyRef = inject(DestroyRef);
  readonly SECTIONS = SpGroupPageSection;

  statsGrid = viewChild.required<AdvertisingStatsGridComponent>(AdvertisingStatsGridComponent);

  tabs = computed<ITab<SpGroupPageSection>[]>(() => [
    {
      label: this.translocoService.translate("sd-strategy-creation.advertised_products"),
      value: SpGroupPageSection.PRODUCTS,
      icon: "package",
      badge: this.strategyGroup()?.asins?.length + "",
      testId: "products",
    },
    {
      label: this.translocoService.translate("common.strategies"),
      value: SpGroupPageSection.CAMPAIGNS,
      icon: "campaign",
      testId: "strategies",
    },
    { label: "Stats", value: SpGroupPageSection.STATS, icon: "chartLine", testId: "stats" },
    {
      label: this.translocoService.translate("page-title.activities"),
      value: SpGroupPageSection.ACTIVITIES,
      icon: "clock",
      testId: "activities",
    },
  ]);

  activeTab = signal(1);
  selectedSection = computed(() => this.tabs()[this.activeTab()].value);

  readonly accountMarketplace = toSignal(this.accountSelectionService.singleAccountMarketplaceSelection$);
  readonly user = toSignal(this.authService.loggedUser$);
  readonly isReadOnly = toSignal<boolean>(this.accountSelectionService.readOnlyMode$);

  private readonly strategyGroup$ = this.route.paramMap.pipe(
    tap(() => this.loadingService.loadingOn()),
    switchMap((params) => {
      const id = Number(params.get("id"));
      return this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.spStrategyService.getStrategyGroups(am.accountId, am.marketplace)),
        map((strategyGroups) => strategyGroups.get(id)),
      );
    }),
    tap((strategyGroup) => this.checkRedirect(strategyGroup)),
    tap(() => this.loadingService.loadingOff()),
    takeUntilDestroyed(this.destroyRef),
  );

  strategyGroup = toSignal(this.strategyGroup$);

  strategiesPerAsin = computed(() => {
    return this.strategyService.getStrategyPerAsin(this.strategyGroup()!);
  });

  strategyGroupName = computed(() => this.strategyGroup()?.strategyGroupName ?? "");

  activityFilter = computed<ActivityFilter[]>(() => {
    return [
      { primaryType: EntityIdType.strategyGroupId, primaryId: this.strategyGroup()!.strategyGroupId },
      ...this.strategyGroup()!
        .brandStrategies.concat(this.strategyGroup()!.keywordStrategies)
        .concat(this.strategyGroup()!.productStrategies)
        .map((s) => ({ primaryType: EntityIdType.strategyId, primaryId: s.strategyId })),
    ];
  });

  syncMessage = toSignal<string | undefined>(
    combineLatest([this.accountSelectionService.singleAccountMarketplaceSelection$, this.strategyGroup$]).pipe(
      switchMap(([accountMarketplace, strategyGroup]) =>
        this.strategyService.getStrategyNextPushHours(strategyGroup!, accountMarketplace!),
      ),
      map((nbHours) => {
        if (!nbHours) return undefined;
        return this.translocoService.translate("sp-strategy-group-page.time_for_update", {
          count: nbHours,
        });
      }),
    ),
  );

  readonly hiddenColumns = ["campaignType", "strategyGroup"];
  strategyGroupStatsFilter = computed(
    () => (stats: GlobalStrategyStats) => stats.strategy?.strategyGroupId == this.strategyGroup()?.strategyGroupId,
  );

  private checkRedirect(strategyGroup: StrategyGroupEx | undefined): void {
    if (!strategyGroup) {
      this.router.navigate(["/advertising/sponsored-product"], { queryParamsHandling: "merge" });
      return;
    }

    if (singleStrategyInStrategyGroup(strategyGroup)) {
      this.router.navigate(
        [
          "/advertising/sponsored-product/strategy-group",
          strategyGroup.strategyGroupId,
          strategyGroup.productStrategies[0].strategyId,
        ],
        { queryParamsHandling: "merge" },
      );
    }
  }

  exportGridCsv(): void {
    const fileName = getCsvFileName(
      "strategy_stats",
      this.accountMarketplace()?.accountGroupName,
      this.accountMarketplace()?.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    this.statsGrid().exportGridCsv(fileName);
  }

  updateStrategyName(newName: string) {
    if (!this.strategyGroup() || !this.accountMarketplace() || newName.trim() === "") return;
    this.spStrategyService
      .updateStrategyGroup({
        accountId: this.strategyGroup()!.accountId!,
        marketplace: this.strategyGroup()!.marketplace!,
        organizationId: this.accountMarketplace()!.resourceOrganizationId!,
        strategyGroupId: this.strategyGroup()!.strategyGroupId!,
        strategyGroupName: newName,
      })
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translocoService.translate("sp-strategy-group-page.strategy_group_name_updated"),
          );
        },
        error: (e: string) => {
          this.toastrService.error(
            e,
            this.translocoService.translate("sp-strategy-group-page.strategy_group_name_update_eror"),
          );
        },
      });
  }
}
